import GrecoReactGrid, {
  standardCellTypeHandlers,
} from "components/grecoreactgrid/GrecoReactGrid";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";
import { insurerReportColumnConfig } from "./insurerReportColumnConfig";
import CustomTextCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomTextCellTypeHandler";
import CustomNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomNumberCellTypeHandler";
import SortableHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SortableHeaderCellTypeHandler";
import SumHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SumHeaderCellTypeHandler";
import OrdinalNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/OrdinalNumberCellTypeHandler";
import TaxonomyCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/TaxonomyCellTypeHandler";
import SelectCellTypeHandler from "./customcelltypehandlers/SelectCellTypeHandler";
import SelectAllCellTypeHandler from "./customcelltypehandlers/SelectAllCellTypeHandler";
import InsurerReportOptionsCellTypeHandler from "./customcelltypehandlers/InsurerReportOptionsCellTypeHandler";
import { DeleteInsurerReportDialog } from "./DeleteInsurerReportDialog";
import { MatchInsurerReportDialog } from "./MatchInsurerReportDialog";
import { ImportInsurerReportDialog } from "./ImportInsurerReportDialog";
import { useTheme } from "@emotion/react";

const InsurerReportGrid = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { taxonomy, grecoCompanies, updatedGrecoCompanies } =
    useAppStoreState();
  const insurerReportsStore = useInsurerReportsStore();
  const [
    {
      insurerReports,
      insurerReportMetadataMap,
      searchTerm,
      fromDateFilter,
      toDateFilter,
      searchDateFilterField,
      searchInsurersSelectedItem,
      insurerReportsSort,
      deleteInsurerReportDialog,
      matchInsurerReportDialog,
      importInsurerReportDialog,
    },
    {
      loadInsurerReports,
      filterInsurerReports,
      setInsurerReportsStoreValues,
      setInsurerReportsStoreValue,
    },
  ] = insurerReportsStore;

  useEffect(() => {
    if (updatedGrecoCompanies !== null && grecoCompanies?.length > 0) {
      loadInsurerReports({
        t,
        grecoCompanies,
        updatedGrecoCompanies,
        taxonomy,
      });
    }
  }, [
    fromDateFilter,
    toDateFilter,
    updatedGrecoCompanies,
    taxonomy,
    searchDateFilterField,
    searchInsurersSelectedItem,
  ]);

  useEffect(() => {
    if (insurerReports) {
      filterInsurerReports({ searchTerm });
    }
    // See BankStatementGrid: entries are intentionally not a dependency.
    // loadInsurerReports commits insurerReports and insurerReportEntries in one
    // setState, so `insurerReports` already covers the load case for child matching.
  }, [searchTerm, insurerReports]);

  const handleChanges = (
    updatedRows: any[],
    updatedRowsMetadataMap: { [key: number]: any }
  ) => {
    setInsurerReportsStoreValues({
      insurerReports: insurerReports.map((entry) => {
        const updatedEntry = updatedRows.find(
          (row) => row.insurerReportId === entry.insurerReportId
        );
        return updatedEntry ? updatedEntry : entry;
      }),
      insurerReportMetadataMap: updatedRowsMetadataMap,
    });
  };

  const cellTypeHandlers = {
    ...standardCellTypeHandlers,
    customtext: new CustomTextCellTypeHandler(),
    customnumber: new CustomNumberCellTypeHandler(),
    taxonomy: new TaxonomyCellTypeHandler(),
    select: new SelectCellTypeHandler(),
    selectall: new SelectAllCellTypeHandler(),
    sortableheader: new SortableHeaderCellTypeHandler(),
    sumheader: new SumHeaderCellTypeHandler(),
    ordinalnumber: new OrdinalNumberCellTypeHandler(),
    insurerreportoptions: new InsurerReportOptionsCellTypeHandler(),
  };

  return (
    <>
      <div style={{ height: "100%", overflow: "auto" }}>
        <div
          data-help="insurer-reports-grid"
          style={{ height: "calc(100% - 10px)", overflow: "scroll" }}
        >
          <GrecoReactGrid
            rows={insurerReports}
            columns={insurerReportColumnConfig}
            idColumn={"insurerReportId"}
            rowsMetadataMap={insurerReportMetadataMap}
            handleChanges={handleChanges}
            cellTypeHandlers={cellTypeHandlers}
            keyColumn="insurerReportId"
            hasSumHeaderRow={false}
            stickyColumns={{
              left: 3,
              right: 1,
              top: 1,
            }}
            context={{
              taxonomy: taxonomy,
              t: t,
              insurerReportsStore: insurerReportsStore,
              theme: theme,
              grecoCompanies: grecoCompanies,
              sort: insurerReportsSort,
              setSort: (sort: any) => {
                setInsurerReportsStoreValue("insurerReportsSort", sort);
              },
            }}
            sort={insurerReportsSort}
          />
        </div>
      </div>
      {deleteInsurerReportDialog.isOpen && <DeleteInsurerReportDialog />}
      {matchInsurerReportDialog.isOpen && <MatchInsurerReportDialog />}
      {importInsurerReportDialog.isOpen && <ImportInsurerReportDialog />}
    </>
  );
};

export default InsurerReportGrid;
